import { Capacitor, registerPlugin } from '@capacitor/core';
import { ApiError } from './api.js';

/**
 * Localização do aparelho, só com o app aberto.
 *   - approximate(): "oficinas perto de você". Pede só a localização APROXIMADA e arredonda para ~1 km
 *     antes de sair do aparelho. Não é guardada em lugar nenhum (nem aqui, nem na API).
 *   - precise(): "Estou na oficina agora" (responsável pela oficina). Pede a PRECISA.
 * No app usa o plugin nativo @capacitor/geolocation (vem do ticket-car-app 1.3.0); no navegador, navigator.geolocation.
 */
const Geo = registerPlugin('Geolocation');
const native = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Geolocation');
const round = (x) => Math.round(x * 100) / 100;

/** 'granted' | 'denied' | 'prompt' | 'unsupported' (para a localização aproximada). */
async function permission() {
  if (native()) {
    const p = await Geo.checkPermissions().catch(() => null);
    const v = p?.coarseLocation ?? p?.location;
    return v === 'granted' ? 'granted' : v === 'denied' ? 'denied' : v ? 'prompt' : 'unsupported';
  }
  if (!('geolocation' in navigator)) return 'unsupported';
  const s = await navigator.permissions?.query({ name: 'geolocation' }).catch(() => null);
  return s?.state ?? 'prompt';
}

function friendly(err) {
  const msg = String(err?.message ?? err ?? '');
  // erro original do plugin (ex.: OS-PLUG-GLOC-0010 tempo esgotado), para o diagnóstico em DEV
  const detail = [err?.code, msg].filter(Boolean).join(' ');
  const make = (code, text) => Object.assign(new ApiError(0, code, text), { detail });
  if (err?.code === 1 || /denied|permission/i.test(msg)) return make('LOCATION_DENIED', 'Permissão de localização negada.');
  if (/disabled|not enabled|turned off/i.test(msg)) return make('LOCATION_OFF', 'A localização do celular está desligada. Ative e tente de novo.');
  return make('LOCATION_UNAVAILABLE', 'Não foi possível obter a localização agora. Tente de novo.');
}

async function read({ precise, fresh = false }) {
  // aproximada aceita uma posição de até 10 min (abre rápido); fresh ("Atualizar") pede uma nova
  const opts = { enableHighAccuracy: precise, timeout: 20_000, maximumAge: precise || fresh ? 0 : 10 * 60_000 };
  if (native()) {
    const perms = await Geo.requestPermissions({ permissions: [precise ? 'location' : 'coarseLocation'] }).catch((e) => {
      throw friendly(e);
    });
    const state = precise ? perms.location : perms.coarseLocation ?? perms.location;
    if (state !== 'granted' && !(precise && perms.coarseLocation === 'granted')) throw new ApiError(0, 'LOCATION_DENIED', 'Permissão de localização negada.');
    // aproximada: em celular antigo o modo econômico (rede/Wi-Fi) pode não fixar ou ser recusado pelo Google Play
    // Services; tenta então o GPS e, por fim, a última posição conhecida. A precisa vai direto ao GPS.
    const attempts = precise
      ? [opts]
      : [opts, { ...opts, enableHighAccuracy: true }, { enableHighAccuracy: false, timeout: 5_000, maximumAge: Infinity }];
    let first;
    const SOURCES = ['rede/Wi-Fi', 'GPS', 'última posição conhecida'];
    for (const [i, o] of attempts.entries()) {
      try {
        const { coords, timestamp } = await Geo.getCurrentPosition(o);
        location.lastFix = { source: precise ? 'GPS' : SOURCES[i], at: timestamp ?? Date.now(), accuracy: coords.accuracy ?? null };
        return coords;
      } catch (e) {
        first ??= e;
        if (/denied|permission/i.test(String(e?.message ?? ''))) break;
      }
    }
    throw friendly(first);
  }
  if (!('geolocation' in navigator)) throw new ApiError(0, 'LOCATION_UNAVAILABLE', 'Este aparelho não informa a localização.');
  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(
      (p) => {
        location.lastFix = { source: 'navegador', at: p.timestamp ?? Date.now(), accuracy: p.coords.accuracy ?? null };
        resolve(p.coords);
      },
      (e) => reject(friendly(e)),
      opts,
    ),
  );
}

export const location = {
  permission,
  /** De onde veio a última posição e quando foi medida (o Início mostra "há X min"). */
  lastFix: null,
  /** O plugin nativo de localização está no app instalado? (diagnóstico em DEV) */
  nativeAvailable: native,

  /** Posição aproximada (~1 km) para ordenar a vitrine. */
  async approximate({ fresh = false } = {}) {
    const c = await read({ precise: false, fresh });
    return { lat: round(c.latitude), lng: round(c.longitude) };
  },

  /** Posição precisa para marcar a oficina no mapa (a API recusa precisão pior que 150 m). */
  async precise() {
    const c = await read({ precise: true });
    return { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy ?? 9999 };
  },
};
